import { ResponseBuilder } from "../core/ResponseBuilder.js";
import type { AssistantResponse, MountainSeverity } from "../types/AssistantResponse.js";
import type { MountainContext } from "../types/MountainContext.js";
import type { MountainCommand } from "../commands/MountainCommand.js";

interface AdviceDefinition {
  readonly message: string;
  readonly severity: MountainSeverity;
  readonly recommendedAction: string;
  readonly limitations: readonly string[];
}

const COMMON_LIMIT = "Consejo general de Beta V1.2: no sustituye criterio profesional, guia, rescate ni atencion medica.";

const WEATHER_RELEVANT = new Set<MountainCommand>([
  "COLD_ACTION",
  "ADD_LAYER",
  "CLOTHING_NOW",
  "WET_CLOTHING",
  "PRESERVE_HEAT",
  "STRONG_WIND",
  "HEAVY_RAIN",
  "STOP_BREAK",
  "HYDRATION",
  "SUN_PROTECTION",
  "BASIC_GEAR_CHECK",
  "RETURN_CHECK",
  "OFFLINE_CAPABILITIES",
  "HIGH_ALTITUDE_GENERAL",
  "SLIPPERY_TERRAIN",
  "THUNDERSTORM_ACTION",
  "LOW_VISIBILITY",
  "PRE_DEPARTURE_CHECK",
]);

function weatherSummary(context: MountainContext): string | null {
  const weather = context.weather;
  if (!weather) return null;
  const parts: string[] = [];
  if (weather.condition) parts.push(weather.condition);
  if (weather.temperatureCelsius !== null) parts.push(`${Math.round(weather.temperatureCelsius)} C`);
  if (weather.windSpeedKph !== null) parts.push(`viento ${Math.round(weather.windSpeedKph)} km/h`);
  if (weather.gustSpeedKph !== null) parts.push(`rafagas ${Math.round(weather.gustSpeedKph)} km/h`);
  if (weather.precipitationProbabilityPercent !== null) parts.push(`lluvia ${Math.round(weather.precipitationProbabilityPercent)} %`);
  if (weather.next6HoursPrecipitationMm !== null) parts.push(`proximas 6 h ${weather.next6HoursPrecipitationMm.toFixed(1)} mm`);
  if (weather.visibilityMeters !== null) parts.push(`visibilidad ${(weather.visibilityMeters / 1000).toFixed(1)} km`);
  if (weather.uvIndex !== null) parts.push(`UV ${weather.uvIndex.toFixed(1)}`);
  return parts.length ? parts.join(", ") : null;
}

const advice: Readonly<Partial<Record<MountainCommand, AdviceDefinition>>> = {
  COLD_ACTION: {
    message: "Si tienes frio, reduce la exposicion, anade abrigo seco si esta disponible y evita permanecer quieto en un lugar expuesto.",
    severity: "CAUTION",
    recommendedAction: "Detente a reevaluar. Protege torso, cabeza, cuello y manos; cambia prendas humedas si puedes hacerlo con seguridad.",
    limitations: [COMMON_LIMIT, "Mountain AI no diagnostica hipotermia ni confirma que sea seguro continuar."],
  },
  ADD_LAYER: {
    message: "Si empiezas a enfriarte o vas a detenerte, suele ser prudente anadir una capa antes de perder mas calor.",
    severity: "INFORMATION",
    recommendedAction: "Prioriza una capa seca y adecuada al viento o lluvia presentes; evita sobrecalentarte durante esfuerzo intenso.",
    limitations: [COMMON_LIMIT, "La recomendacion no conoce por si sola tu ropa real ni tu estado fisico."],
  },
  CLOTHING_NOW: {
    message: "La ropa debe adaptarse a frio, viento, humedad y nivel de esfuerzo. En general conviene trabajar por capas y mantener una capa seca de respaldo.",
    severity: "INFORMATION",
    recommendedAction: "Ajusta abrigo, cortaviento e impermeable segun las condiciones reales y tu sensacion termica.",
    limitations: [COMMON_LIMIT, "Sin datos de clima y equipo no puede indicar una prenda exacta."],
  },
  WET_CLOTHING: {
    message: "La ropa mojada puede acelerar la perdida de calor, especialmente con viento o bajas temperaturas.",
    severity: "CAUTION",
    recommendedAction: "Protegete de lluvia y viento y cambia a una capa seca si dispones de ella y puedes hacerlo de forma segura.",
    limitations: [COMMON_LIMIT],
  },
  PRESERVE_HEAT: {
    message: "Para conservar calor, reduce exposicion al viento y humedad, usa capas secas y evita pausas largas en lugares expuestos.",
    severity: "INFORMATION",
    recommendedAction: "Abriga primero el torso y protege cabeza, cuello y manos; mantente seco y reevalúa si el frio aumenta.",
    limitations: [COMMON_LIMIT],
  },
  STRONG_WIND: {
    message: "El viento fuerte aumenta la perdida de calor y puede afectar equilibrio y seguridad en zonas expuestas.",
    severity: "CAUTION",
    recommendedAction: "Reduce el ritmo, usa proteccion cortaviento y evita crestas o pasos expuestos si las condiciones empeoran.",
    limitations: [COMMON_LIMIT, "No se esta midiendo aqui la velocidad real del viento."],
  },
  HEAVY_RAIN: {
    message: "Con lluvia, la prioridad es mantener secas las capas de abrigo y proteger equipo esencial.",
    severity: "CAUTION",
    recommendedAction: "Usa proteccion impermeable, evita enfriarte durante pausas y reevalúa terreno, visibilidad y ruta.",
    limitations: [COMMON_LIMIT, "No se esta evaluando aqui riesgo de crecidas, rayos o deslizamientos."],
  },
  STOP_BREAK: {
    message: "Durante una pausa puedes enfriarte rapidamente aunque hayas tenido calor caminando.",
    severity: "INFORMATION",
    recommendedAction: "Busca un punto protegido, anade abrigo antes de sentir frio y revisa agua, energia, ruta y bateria.",
    limitations: [COMMON_LIMIT],
  },
  NIGHTFALL: {
    message: "Si esta oscureciendo, disminuye el margen para resolver errores de navegacion y aumenta la dependencia de luz y bateria.",
    severity: "CAUTION",
    recommendedAction: "Revisa ruta, distancia restante, luz disponible, bateria y una opcion conservadora de regreso antes de seguir.",
    limitations: [COMMON_LIMIT, "No asume que exista una salida segura ni calcula por si solo la hora de oscuridad."],
  },
  SAVE_BATTERY: {
    message: "Para conservar bateria, reduce brillo y uso de pantalla y evita funciones no esenciales mientras mantienes disponibles las funciones necesarias de navegacion y emergencia.",
    severity: "INFORMATION",
    recommendedAction: "Prioriza GPS, ruta y comunicaciones necesarias; usa ahorro de energia si no interfiere con el seguimiento que necesitas.",
    limitations: [COMMON_LIMIT, "El ahorro real depende del telefono, bateria, cobertura y configuracion."],
  },
  HYDRATION: {
    message: "En actividad de montaña conviene beber de forma regular y ajustar la ingesta al esfuerzo, clima y disponibilidad de agua segura.",
    severity: "INFORMATION",
    recommendedAction: "No esperes a estar muy sediento para revisar tu agua disponible; evita consumir agua de fuente dudosa sin tratamiento adecuado.",
    limitations: [COMMON_LIMIT, "No proporciona una cantidad medica individual ni evalua condiciones personales."],
  },
  FOOD_ENERGY: {
    message: "Si notas una caida de energia, una pausa breve con alimento disponible y agua puede ayudarte a reevaluar el esfuerzo restante.",
    severity: "INFORMATION",
    recommendedAction: "Come algo que ya toleres bien, hidrata y revisa si el cansancio esta aumentando antes de continuar.",
    limitations: [COMMON_LIMIT, "No evalua causas medicas de fatiga."],
  },
  SUN_PROTECTION: {
    message: "En altura la exposicion solar puede ser intensa incluso con temperatura baja o nubes.",
    severity: "INFORMATION",
    recommendedAction: "Usa proteccion solar adecuada, gorra o sombrero, gafas apropiadas e hidratacion segun tus necesidades.",
    limitations: [COMMON_LIMIT],
  },
  BASIC_GEAR_CHECK: {
    message: "Un chequeo basico incluye abrigo, impermeable, agua, alimento, luz, bateria, navegacion y elementos personales esenciales.",
    severity: "INFORMATION",
    recommendedAction: "Confirma que el equipo critico esta accesible y funciona antes de alejarte mas del punto de retorno.",
    limitations: [COMMON_LIMIT, "La lista exacta depende de ruta, clima, duracion y experiencia."],
  },
  LOST_ROUTE: {
    message: "Si crees que estas perdido, evita seguir avanzando sin una referencia fiable.",
    severity: "WARNING",
    recommendedAction: "Detente, revisa tu ultima posicion confiable, ruta registrada, precision GPS y el ultimo punto conocido antes de decidir cualquier movimiento.",
    limitations: [COMMON_LIMIT, "Mountain AI no puede garantizar una direccion de salida segura."],
  },
  RETURN_CHECK: {
    message: "Antes de decidir regresar conviene revisar ruta, luz restante, clima disponible, bateria, desviacion y estado del grupo.",
    severity: "CAUTION",
    recommendedAction: "Prioriza una decision conservadora si las condiciones empeoran o tienes menos margen de seguridad.",
    limitations: [COMMON_LIMIT, "No garantiza que el regreso sea seguro."],
  },
  GPS_UNRELIABLE_ACTION: {
    message: "Si el GPS es poco preciso, evita usar una posicion incierta para decisiones finas de navegacion.",
    severity: "CAUTION",
    recommendedAction: "Detente, espera una mejor lectura si es posible y contrasta con ruta, terreno y referencias confiables disponibles.",
    limitations: [COMMON_LIMIT],
  },
  OFFLINE_CAPABILITIES: {
    message: "Sin Internet, Mountain AI puede usar los datos locales disponibles, como GPS, ruta, progreso, desviacion y reglas offline ya cargadas.",
    severity: "OFFLINE_LIMITED",
    recommendedAction: "Comprueba que la ruta y datos necesarios esten disponibles localmente y no asumas que clima o comunicaciones estan actualizados.",
    limitations: [COMMON_LIMIT, "Las funciones que requieren red pueden no estar disponibles o quedar desactualizadas."],
  },
  HIGH_ALTITUDE_GENERAL: {
    message: "A mayor altitud pueden aumentar frio, viento, radiacion y esfuerzo. No conviene basar una decision solo en el numero de altitud.",
    severity: "CAUTION",
    recommendedAction: "Reduce exigencia si te sientes peor y no sigas ascendiendo si aparecen sintomas nuevos o empeoran; considera descender y buscar ayuda si la situacion preocupa.",
    limitations: [COMMON_LIMIT, "Mountain AI no diagnostica mal de altura ni otras condiciones medicas."],
  },
  SLIPPERY_TERRAIN: {
    message: "En terreno resbaloso, la velocidad y una ruta agresiva aumentan el riesgo de caida.",
    severity: "CAUTION",
    recommendedAction: "Reduce el ritmo, aumenta estabilidad y evita pasos expuestos si no tienes margen suficiente para cruzarlos con seguridad.",
    limitations: [COMMON_LIMIT, "No evalua visualmente la firmeza ni inclinacion real del terreno."],
  },
  THUNDERSTORM_ACTION: {
    message: "Truenos o rayos indican una situacion potencialmente peligrosa en terreno de montana, especialmente en puntos altos y expuestos.",
    severity: "WARNING",
    recommendedAction: "Interrumpe el avance por cumbres, crestas y zonas muy expuestas y busca una opcion menos expuesta si puedes hacerlo sin aumentar el riesgo.",
    limitations: [COMMON_LIMIT, "No sustituye un protocolo local de tormentas ni datos meteorologicos en tiempo real."],
  },
  LOW_VISIBILITY: {
    message: "Con niebla o visibilidad reducida es mas facil perder referencias y desviarse de la ruta.",
    severity: "CAUTION",
    recommendedAction: "Reduce el ritmo, verifica posicion y precision GPS con frecuencia y evita avanzar si no puedes confirmar una referencia fiable.",
    limitations: [COMMON_LIMIT],
  },
  ORIENTATION_CHECK: {
    message: "Antes de moverte, revisa posicion, precision GPS, ruta prevista, direccion general y un punto de referencia conocido.",
    severity: "INFORMATION",
    recommendedAction: "Si los datos se contradicen o la posicion es poco fiable, detente y resuelve la incertidumbre antes de avanzar.",
    limitations: [COMMON_LIMIT],
  },
  NEED_HELP: {
    message: "Mountain AI puede ayudarte a revisar datos y opciones, pero no puede prometer rescate ni confirmar que una comunicacion de emergencia haya sido enviada.",
    severity: "WARNING",
    recommendedAction: "Si existe una emergencia real, usa los medios de emergencia disponibles en tu dispositivo, grupo o entorno y prioriza permanecer en una situacion lo mas segura posible.",
    limitations: [COMMON_LIMIT, "Esta Beta no realiza llamadas, mensajes SOS ni contacto automatico con rescate."],
  },
  PRE_DEPARTURE_CHECK: {
    message: "Antes de salir, revisa ruta, clima disponible, abrigo, impermeable, agua, alimento, luz, bateria, navegacion y plan de regreso.",
    severity: "INFORMATION",
    recommendedAction: "Ajusta el equipo a duracion, altitud, terreno y condiciones previstas, y deja margen para cambios.",
    limitations: [COMMON_LIMIT, "La lista no reemplaza una planificacion especifica de la actividad."],
  },
};

export class MountainAdviceRules {
  supports(command: MountainCommand): boolean {
    return advice[command] !== undefined;
  }

  evaluate(command: MountainCommand, context: MountainContext): AssistantResponse | null {
    const definition = advice[command];
    if (!definition) return null;

    const dataUsed: string[] = [];
    if (command === "GPS_UNRELIABLE_ACTION" && context.gps) dataUsed.push("gps.accuracyMeters");
    if ((command === "LOST_ROUTE" || command === "RETURN_CHECK" || command === "ORIENTATION_CHECK") && context.route) dataUsed.push("route");
    if ((command === "LOST_ROUTE" || command === "ORIENTATION_CHECK") && context.gps) dataUsed.push("gps");
    if (command === "OFFLINE_CAPABILITIES") dataUsed.push("offline");

    const useWeather = WEATHER_RELEVANT.has(command);
    const summary = useWeather ? weatherSummary(context) : null;
    if (summary) dataUsed.push("weather");

    let severity = definition.severity;
    if ((context.offline || context.weather?.cached) && severity === "INFORMATION") severity = "OFFLINE_LIMITED";

    const limitations = [...definition.limitations];
    if (summary) {
      const misleading = [
        "No se esta midiendo aqui la velocidad real del viento.",
        "Sin datos de clima y equipo no puede indicar una prenda exacta.",
      ];
      for (const item of misleading) {
        const index = limitations.indexOf(item);
        if (index >= 0) limitations.splice(index, 1);
      }
      if (context.weather?.cached) {
        limitations.push("El clima proviene de la ultima copia guardada en el dispositivo y puede estar desactualizado.");
      }
    } else if (useWeather) {
      limitations.push("No hay datos meteorologicos disponibles en este contexto; el consejo se mantiene general.");
    }

    return ResponseBuilder.build({
      message: summary ? `${definition.message} Datos de THERA: ${summary}.` : definition.message,
      severity,
      confidence: summary ? 0.9 : 0.85,
      dataUsed,
      dataTimestamp: summary && context.weather ? context.weather.timestamp : context.timestamp,
      offline: context.offline,
      recommendedAction: definition.recommendedAction,
      limitations,
      source: "ADVICE_RULE",
    });
  }
}
